// Wraps children with a Joyride guided tour, using the provided steps

import { useEffect, useId, useState } from 'react';
import Joyride, { STATUS } from 'react-joyride';
import { toast } from 'react-toastify';
import { userSetTourStatus } from '../utils/firebase';
import { useFirebase } from './FirebaseContextProvider';
import { TourStatuses } from '../utils/enums';
import './GuidedTour.scss';

// Joyride instances know nothing of each other, so a card and a list nested in
// it paint two overlays at once. One slot serializes them: the first to claim
// runs, the rest wait for it to release.
const slot = { taken: null, waiting: new Set() };

function useTourSlot(wantsToRun) {
  const token = useId();
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (!wantsToRun) return undefined;

    const tryClaim = () => {
      if (!slot.taken) slot.taken = token;
      setIsActive(slot.taken === token);
    };

    slot.waiting.add(tryClaim);
    tryClaim();

    return () => {
      slot.waiting.delete(tryClaim);
      if (slot.taken !== token) return;
      slot.taken = null;
      slot.waiting.forEach((claim) => claim());
    };
  }, [wantsToRun, token]);

  return isActive;
}

export default function GuidedTour({
  steps,
  run,
  children,
  continuous = true,
  stepIndex,
  callback,
  tourKey,
}) {
  const { user } = useFirebase(); // Get the current authenticated user

  // Determine if the user has already completed or skipped this tour
  const hasSeenTour =
    user?.tourStatusesLoaded && Array.isArray(user?.tourStatuses)
      ? user.tourStatuses.some(
          ({ id, status }) =>
            id === tourKey &&
            (status === TourStatuses.COMPLETED ||
              status === TourStatuses.SKIPPED),
        )
      : true;

  // Determine if we should run the tour: if `run` prop is boolean, respect it; otherwise default to true (unless already seen)
  const shouldRunTour = !hasSeenTour && (typeof run === 'boolean' ? run : true);

  // A stepless tour must not claim the slot: Joyride never starts it, so it
  // would never release
  const wantsToRun = shouldRunTour && steps?.length > 0;
  const hasSlot = useTourSlot(wantsToRun);

  // Unified Joyride callback to report tour status changes to the backend
  const handleJoyrideCallback = async (data) => {
    if (user) {
      const { status } = data;
      let newStatus;

      // Determine the appropriate status to send
      switch (status) {
        case STATUS.FINISHED:
          newStatus = TourStatuses.COMPLETED;
          break;
        case STATUS.SKIPPED:
          newStatus = TourStatuses.SKIPPED;
          break;
        default:
          break;
      }

      // Send status update if applicable
      if (newStatus) {
        try {
          await userSetTourStatus({
            id: user.id,
            tour: tourKey,
            status: newStatus,
          });
        } catch (err) {
          toast.error(err.message);
        }
      }
    }

    // Forward the event to the external callback if provided
    if (callback) {
      callback(data);
    }
  };

  return (
    <>
      <Joyride
        steps={steps?.map((s) => ({ ...s, disableBeacon: true }))}
        continuous={continuous}
        showProgress
        showSkipButton={continuous}
        hideBackButton={!continuous}
        locale={{ last: 'Close' }}
        run={wantsToRun && hasSlot}
        stepIndex={stepIndex}
        callback={handleJoyrideCallback}
        scrollOffset={210}
      />
      {children}
    </>
  );
}
